import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SyncIcon from '@mui/icons-material/Sync';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import api from '../../services/api.js';
import { contentLessonChannels } from '../../data/lessonChannels.js';
import { ckConfirm } from '@NP-Webber/crm-kit/dist/next/index.js';
import { introUploadMessage } from './introUploadMessage.js';

const AUDIO_EXT_RE = /\.(mp3|wav|ogg|m4a|aac|flac|wma|webm)$/i;
const DEFAULT_MOVE_CHANNEL = 'shinun_blik';
const MOVE_CHANNELS = contentLessonChannels().filter((c) => c.id !== 'all_lessons');

/**
 * @param {{ embedded?: boolean }} props
 */
const IvrIntroManager = ({ embedded = false }) => {
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [uploading, setUploading] = useState(false);
  const [ensuring, setEnsuring] = useState(false);
  const [title, setTitle] = useState('');
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingUrl, setPendingUrl] = useState('');
  const [playingId, setPlayingId] = useState(null);
  const [moveClip, setMoveClip] = useState(null);
  const [moveForm, setMoveForm] = useState({ channel: DEFAULT_MOVE_CHANNEL, title: '' });
  const [moving, setMoving] = useState(false);
  const fileRef = useRef(null);
  const audioElRef = useRef(null);

  const stopAudio = useCallback(() => {
    if (audioElRef.current) {
      audioElRef.current.pause();
      audioElRef.current = null;
    }
    setPlayingId(null);
  }, []);

  useEffect(() => () => {
    stopAudio();
    if (pendingUrl) URL.revokeObjectURL(pendingUrl);
  }, [stopAudio, pendingUrl]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/mishtameret-intro');
      setClips(data.clips || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'שגיאה בטעינה');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const playBlob = async (id, blobPromise) => {
    if (playingId === id && audioElRef.current) {
      stopAudio();
      return;
    }
    stopAudio();
    try {
      const { data: blob, status } = await blobPromise;
      if (blob.type?.includes('application/json') || status >= 400) {
        let text = 'שגיאה בהשמעה';
        try { text = JSON.parse(await blob.text()).error || text; } catch { /* ignore */ }
        setError(text);
        return;
      }
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => {
        setPlayingId(null);
        URL.revokeObjectURL(url);
        audioElRef.current = null;
      };
      audio.onerror = () => {
        setError('לא ניתן להשמיע את הקובץ');
        URL.revokeObjectURL(url);
        audioElRef.current = null;
        setPlayingId(null);
      };
      audioElRef.current = audio;
      setPlayingId(id);
      await audio.play();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'שגיאה בהשמעה');
      setPlayingId(null);
    }
  };

  const playClip = (clipId) => {
    playBlob(
      `clip-${clipId}`,
      api.get(`/mishtameret-intro/clips/${clipId}/audio`, { responseType: 'blob' })
    );
  };

  const playPending = () => {
    if (!pendingUrl) return;
    if (playingId === 'pending' && audioElRef.current) {
      stopAudio();
      return;
    }
    stopAudio();
    const audio = new Audio(pendingUrl);
    audio.onended = () => {
      setPlayingId(null);
      audioElRef.current = null;
    };
    audioElRef.current = audio;
    setPlayingId('pending');
    audio.play().catch(() => {
      setError('לא ניתן להשמיע את הקובץ שנבחר');
      setPlayingId(null);
    });
  };

  const clearPending = () => {
    if (pendingUrl) URL.revokeObjectURL(pendingUrl);
    setPendingFile(null);
    setPendingUrl('');
    if (playingId === 'pending') stopAudio();
    if (fileRef.current) fileRef.current.value = '';
  };

  const onFileSelected = (file) => {
    if (!file) return;
    if (!file.type?.startsWith('audio/') && !AUDIO_EXT_RE.test(file.name || '')) {
      setError('יש להעלות קובץ שמע');
      return;
    }
    if (pendingUrl) URL.revokeObjectURL(pendingUrl);
    setPendingFile(file);
    setPendingUrl(URL.createObjectURL(file));
    setError('');
    if (!title.trim()) setTitle(file.name.replace(AUDIO_EXT_RE, ''));
  };

  const toggleActive = async (clip) => {
    try {
      const { data } = await api.patch(`/mishtameret-intro/clips/${clip._id}`, {
        active: !clip.active,
      });
      setClips((prev) => prev.map((c) => (c._id === data._id ? data : c)));
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בעדכון');
    }
  };

  const removeClip = async (clip) => {
    if (!(await ckConfirm({ title: 'מחיקת הקלטה', message: `למחוק את «${clip.title || 'הקלטה'}»?`, confirmLabel: 'מחיקה' }))) return;
    try {
      await api.delete(`/mishtameret-intro/clips/${clip._id}`);
      setClips((prev) => prev.filter((c) => c._id !== clip._id));
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה במחיקה');
    }
  };

  const openMove = (clip) => {
    setMoveForm({ channel: DEFAULT_MOVE_CHANNEL, title: clip.title || '' });
    setMoveClip(clip);
  };

  const submitMove = async () => {
    if (!moveClip) return;
    setMoving(true);
    setError('');
    setMsg('');
    try {
      await api.post(`/mishtameret-intro/clips/${moveClip._id}/move`, moveForm);
      if (playingId === `clip-${moveClip._id}`) stopAudio();
      setClips((prev) => prev.filter((c) => c._id !== moveClip._id));
      const label = MOVE_CHANNELS.find((c) => c.id === moveForm.channel)?.label || moveForm.channel;
      setMsg(`ההקלטה הועברה ל«${label}» והוסרה מהפתיח`);
      setMoveClip(null);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בהעברה');
      setMoveClip(null);
    } finally {
      setMoving(false);
    }
  };

  const onUpload = async () => {
    if (!pendingFile) {
      setError('בחרו קובץ להעלאה');
      return;
    }
    setUploading(true);
    setError('');
    setMsg('');
    try {
      const fd = new FormData();
      fd.append('audio', pendingFile);
      if (title.trim()) fd.append('title', title.trim());
      const { data } = await api.post('/mishtameret-intro/clips', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setTitle('');
      clearPending();
      const result = introUploadMessage(data);
      if (result.failed) setError(result.text);
      else setMsg(result.text);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בהעלאה');
    } finally {
      setUploading(false);
    }
  };

  const ensureYemot = async () => {
    setEnsuring(true);
    setError('');
    setMsg('');
    try {
      await api.post('/mishtameret-intro/ensure-yemot');
      setMsg('שלוחת ניהול 77/9 עודכנה בימות');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בחיבור לימות');
    } finally {
      setEnsuring(false);
    }
  };

  const byNewest = (a, b) => new Date(b.createdAt) - new Date(a.createdAt);
  const phoneClips = clips.filter((c) => c.source === 'phone').sort(byNewest);
  // שיעור היום מערוץ (source=channel) מנוהל רק במסך העיצוב החדש
  const summaryClips = clips.filter((c) => c.source !== 'phone' && c.source !== 'channel').sort(byNewest);
  const formatDate = (d) => (d
    ? new Date(d).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' })
    : '');

  const renderClip = (clip) => (
    <Box
      key={clip._id}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        opacity: clip.active ? 1 : 0.55,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap fontWeight={500}>{clip.title || 'ללא כותרת'}</Typography>
        <Typography variant="caption" color="text.secondary">
          {formatDate(clip.createdAt)}
          {' · '}
          <span dir="ltr">{clip.yemotPath}</span>
        </Typography>
      </Box>
      <IconButton
        size="small"
        color="primary"
        onClick={() => playClip(clip._id)}
        title="השמע"
      >
        {playingId === `clip-${clip._id}` ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
      </IconButton>
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={Boolean(clip.active)}
            onChange={() => toggleActive(clip)}
          />
        }
        label=""
      />
      <Button size="small" startIcon={<DriveFileMoveIcon fontSize="small" />} onClick={() => openMove(clip)}>
        מעבר
      </Button>
      <IconButton size="small" color="error" onClick={() => removeClip(clip)}>
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ p: embedded ? 1 : 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 }, maxWidth: embedded ? 'none' : 820, mx: embedded ? 0 : 'auto' }}>
      {!embedded && (
        <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>פתיח IVR (77)</Typography>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        לפני «ברוכים הבאים...» (001) מושמעת קודם הודעת הטלפון, ואחריה הסיכומים הפעילים.
        הודעת טלפון (77 → 9 → סיסמה → 3): הודעה אחת פעילה; בהקלטה חדשה הקודמת עוברת ל«א בליק אויפן בלעט» (ואם ההעברה האוטומטית כבויה — נמחקת).
        סיכום שמועלה מכאן מופעל אוטומטית, והסיכום הפעיל הקודם עובר ל«א בליק אויפן בלעט» (אלא אם ההעברה האוטומטית כבויה — אז הוא רק נכבה).
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert>}

      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
        <Button
          size="small"
          startIcon={ensuring ? <CircularProgress size={14} /> : <SyncIcon />}
          onClick={ensureYemot}
          disabled={ensuring}
        >
          סנכרן שלוחת ניהול בימות
        </Button>
      </Stack>

      <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Typography fontWeight={600} sx={{ mb: 1 }}>העלאת סיכום</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <TextField
            size="small"
            label="כותרת (אופציונלי)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <input
            ref={fileRef}
            type="file"
            accept="audio/*,.mp3,.wav,.ogg,.m4a"
            hidden
            onChange={(e) => onFileSelected(e.target.files?.[0])}
          />
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => fileRef.current?.click()}
          >
            בחר קובץ
          </Button>
          {pendingFile && (
            <>
              <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                {pendingFile.name}
              </Typography>
              <IconButton size="small" color="primary" onClick={playPending} title="השמע לפני העלאה">
                {playingId === 'pending' ? <StopIcon /> : <PlayArrowIcon />}
              </IconButton>
              <Button
                variant="contained"
                disabled={uploading}
                startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : null}
                onClick={onUpload}
              >
                העלה
              </Button>
              <Button size="small" onClick={clearPending} disabled={uploading}>בטל</Button>
            </>
          )}
        </Stack>
      </Box>

      <Typography fontWeight={600} sx={{ mb: 1 }}>הודעת טלפון (9/3)</Typography>
      {phoneClips.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>אין הודעת טלפון. להקלטה: 77 → 9 → סיסמה → 3.</Alert>
      ) : (
        <Stack spacing={1} sx={{ mb: 3 }}>{phoneClips.map(renderClip)}</Stack>
      )}

      <Typography fontWeight={600} sx={{ mb: 1 }}>
        סיכומים ({summaryClips.length}) — מהחדש לישן
      </Typography>
      {summaryClips.length === 0 ? (
        <Alert severity="info">אין סיכומים. העלו קובץ למעלה.</Alert>
      ) : (
        <Stack spacing={1}>{summaryClips.map(renderClip)}</Stack>
      )}

      <Dialog open={!!moveClip} onClose={() => !moving && setMoveClip(null)} fullWidth maxWidth="xs">
        <DialogTitle>העברת הקלטה לערוץ</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select
              size="small"
              label="ערוץ יעד"
              value={moveForm.channel}
              onChange={(e) => setMoveForm((f) => ({ ...f, channel: e.target.value }))}
            >
              {MOVE_CHANNELS.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.label} ({c.ivrPath})</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="כותרת השיעור"
              value={moveForm.title}
              onChange={(e) => setMoveForm((f) => ({ ...f, title: e.target.value }))}
            />
            <Typography variant="caption" color="text.secondary">
              ההקלטה תתווסף כשיעור פעיל בראש הערוץ ותוסר מהפתיח.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveClip(null)} disabled={moving}>ביטול</Button>
          <Button
            variant="contained"
            onClick={submitMove}
            disabled={moving || !moveForm.channel}
            startIcon={moving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            העברה
          </Button>
        </DialogActions>
      </Dialog>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        אחרי הרשימה למעלה מושמע תמיד 001 («ברוכים הבאים...»), וביום שבו נפתח מבחן גם 243 («המבחנים נפתחו במערכת»). לא משתמשים ב-M0001.
      </Typography>
    </Box>
  );
};

export default IvrIntroManager;
